import React from 'react';
import {
  GetSocialAnalyticsSummaryWithPreviousPeriodQuery,
  PostInsights,
  MemberInsights,
} from '@frontend/gql/social/graphql';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { DateType } from '../../types';
import { useTimeSeriesData } from '../../hooks/use-time-series-data';
import { useAnalyticsParams } from '../../hooks/useAnalyticsParams';
import PostsCard from '../post-card';
import MembersCard from '../members-card';
import ReachCard from '../reach-card';
import ImpressionsCard from '../impressions-card';
import EngagementCard from '../engagement-card';
import TMVCard from '../tmv-card';
import FollowerGrowthCard from '../follower-growth-card';
import TMVChartCard from '../tmv-chart-card';
import { SavesMetric, CommentsMetric, SharesMetric, LikesMetric, ImpressionRateMetric } from '../secondary-metrics';
import TopPerformersRow from './top-performers-row';
import TopCreatorsRow from './top-creators-row';
import { EngagementRateMetric } from '../secondary-metrics/EngagementRateMetric';

interface SummaryTabProps {
  data: GetSocialAnalyticsSummaryWithPreviousPeriodQuery | undefined;
  isLoading: boolean;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  clientId?: string;
  topPosts?: PostInsights[];
  topPostsFetching?: boolean;
  topCreators?: MemberInsights[];
  topCreatorsFetching?: boolean;
  initialized: boolean;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({
  data,
  isLoading,
  startDate,
  endDate,
  dateType,
  clientId,
  topPosts,
  topPostsFetching,
  topCreators,
  topCreatorsFetching,
  initialized,
}) => {
  // 📊 Extract time series data from the GraphQL query
  // This data comes from GetSocialAnalyticsSummaryWithPreviousPeriod query which includes:
  // - Current period insights (totalInsights)
  // - Previous period insights (previousPeriodTotalInsights) for comparison
  // - Post type breakdown (postTypes) for trend analysis
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  const previousPeriodTotalInsights = data?.client?.socialAnalytics?.previousPeriodTotalInsights;
  const postTypes = data?.client?.socialAnalytics?.postTypes;

  // 📈 Get analytics params for time series query
  const { queryVariables } = useAnalyticsParams({
    clientId: clientId || '',
    defaultVariables: {
      dateType: dateType || 'last6Months',
      postTypes: ['INSTAGRAM_POST', 'INSTAGRAM_REEL', 'TIKTOK_VIDEO'],
      includeEstimates: true,
      networks: ['INSTAGRAM', 'TIKTOK', 'YOUTUBE', 'PINTEREST'],
      projectIds: [],
      groupIds: [],
      segments: [],
      mentionSources: [],
      memberIds: [],
      postDateIntervalEnabled: false,
      lifetimeInsightsEnabled: false,
      viewMode: 'table',
    },
  });

  // 📊 Fetch time series data for trend charts
  const { data: timeSeriesData } = useTimeSeriesData(queryVariables, initialized);

  if (isLoading) {
    return (
      <div className="p-4 space-y-6">
        {/* Primary metrics skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
        {/* Secondary metrics skeleton */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Chart cards skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
        {/* New section skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} className="h-80 w-full" />
            ))}
          </div>
        </div>
        {/* Top Performers skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
        {/* Top Creators skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* 📈 Primary metrics with time series comparison */}
      {/* These cards receive both current and previous period data for trend analysis */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <PostsCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
        <MembersCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
        <ImpressionsCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
        <EngagementCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
        <ReachCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
        <TMVCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          showComparisonTooltip
        />
      </section>

      {/* 📊 Secondary metrics from current period */}
      <Card>
        <CardContent className="p-4">
          <div
            className="grid grid-cols-2 lg:grid-cols-6 gap-4"
            role="region"
            aria-label="Secondary social media metrics"
          >
            <SavesMetric data={totalInsights} />
            <LikesMetric data={totalInsights} />
            <CommentsMetric data={totalInsights} />
            <SharesMetric data={totalInsights} />
            <EngagementRateMetric data={totalInsights} />
            <ImpressionRateMetric data={totalInsights} />
          </div>
        </CardContent>
      </Card>

      {/* 📈 Chart cards with time series visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" role="region" aria-label="Analytics charts">
        <FollowerGrowthCard
          className="col-span-1"
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          timeSeriesData={
            timeSeriesData?.accountFollowersBreakdown
              ? { accountFollowersBreakdown: timeSeriesData.accountFollowersBreakdown }
              : undefined
          }
        />
        {/* TMV breakdown by post types - uses postTypes data from the query */}
        <TMVChartCard className="col-span-1" data={postTypes} />
      </div>

      {/*
        ISSUE: EPD-1772 - Data Mismatch in Key Metric Trends Section

        PROBLEM:
        The Key Metric Trends section displays different numbers for Impressions, Reach, etc.
        compared to the summary cards above, creating user confusion and data inconsistency.

        ROOT CAUSE:
        - Summary cards use aggregated data from the main query
        - Trend charts incorrectly infer time series data on the client side
        - This leads to calculation discrepancies between the two data sources

        SOLUTION REQUIRED:
        - Backend needs to provide proper time series data via API
        - Implement daily diff tables for accurate trend calculations
        - Remove client-side data inference logic

        STATUS: Temporarily removed until backend data structure is updated
        TODO: Re-enable once API provides consistent time series data
      */}
      {/* 🎯 Key Metric Trends Section - Time series visualization */}
      {/* <div className="space-y-6" role="region" aria-label="Key metric trends">
      {/* Header */}
      {/* <h2 className="font-semibold text-gray-900" style={{ fontSize: '20px' }}>Key Metric Trends</h2>

      {/* Three trend charts showing time series data over the selected period */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ReachTrendsCard
          dateType={dateType}
          timeSeriesData={timeSeriesData?.reach}
          hasData={!!timeSeriesData?.reach?.length}
        />
        <ImpressionsTrendsCard
          dateType={dateType}
          timeSeriesData={timeSeriesData?.impressions}
          hasData={!!timeSeriesData?.impressions?.length}
        />
        <EngagementTrendsCard
          dateType={dateType}
          timeSeriesData={timeSeriesData?.engagement}
          hasData={!!timeSeriesData?.engagement?.length}
        />
      </div>
    </div> */}

      {/* Add Top Performers Row below Key Metric Trends */}
      <TopPerformersRow topPosts={topPosts} topPostsFetching={topPostsFetching} />
      {/* Add Top Creators Row below Top Performers Row */}
      <TopCreatorsRow topCreators={topCreators} topCreatorsFetching={topCreatorsFetching} />
    </div>
  );
};
